// D&D Beyond homebrew magic item form vocabulary, in one place.
//
// Plain arrays with no dependencies, because three different callers need
// them: shared/ddb.js (the zod validator every write path and the MCP
// deploy_item schema use), src/components/forms/ItemForm.jsx (the dropdowns),
// and tests/ddb-extension.test.js (which checks ddb-extension/importer.js
// understands every field listed here). Keep zod out of this file: the browser
// imports it.
//
// Option text is D&D Beyond's VISIBLE option text, exactly as its forms show
// it. The extension matches by that text, so a value that is not on DDB's list
// stops the import before anything is submitted. Lists were captured from the
// live forms (see ddb-extension/README.md); add to them when DDB adds options.

export const DDB_RARITIES = [
  'Common',
  'Uncommon',
  'Rare',
  'Very Rare',
  'Legendary',
  'Artifact',
  'Varies',
  'Unknown Rarity',
];

export const DDB_BASE_TYPES = ['Item', 'Armor', 'Weapon'];

// Only meaningful when baseType is "Item".
export const DDB_MAGIC_ITEM_TYPES = ['Wondrous Item', 'Rod', 'Scroll', 'Staff', 'Wand', 'Ring', 'Potion'];

export const DDB_DEX_BONUS = ['Full Modifier', 'Max 2', 'None'];

export const DDB_STEALTH_CHECK = ['None', 'Disadvantage'];

export const DDB_CHARGE_RESETS = ['Short Rest', 'Long Rest', 'Dawn', 'Other', 'None', 'Consumable'];

export const DDB_ABILITY_SCORES = ['STR', 'DEX', 'CON', 'INT', 'WIS', 'CHA'];

export const DDB_DIE_TYPES = ['d4', 'd6', 'd8', 'd10', 'd12', 'd20'];

export const DDB_ADDITIONAL_BONUS_TYPES = ['Proficiency Bonus', 'Attuned Item Count', 'Spell Level'];

export const DDB_DURATION_UNITS = ['Round', 'Minute', 'Hour', 'Day'];

// Suggestions only, NOT validated: DDB's modifier Type list has 37 entries and
// only these have been captured. Subtypes depend on the type and are loaded by
// the page, so they are free text too. The extension still refuses a value DDB
// does not offer, before submitting.
export const DDB_MODIFIER_TYPE_SUGGESTIONS = [
  'Bonus',
  'Damage',
  'Advantage',
  'Disadvantage',
  'Resistance',
  'Immunity',
  'Vulnerability',
  'Sense',
  'Set',
  'Proficiency',
  'Language',
  'Expertise',
  'Half Proficiency',
  'Feat',
];

// Every key a stored ddb_spec may carry, per section. `description` on the
// item is the site's own addition: an optional markdown override for the text
// sent to DDB (otherwise built from full_description) — the extension receives
// it as HTML under the same key.
export const DDB_ITEM_FIELDS = [
  'version',
  'rarity',
  'baseType',
  'magicItemType',
  'baseArmor',
  'dexBonus',
  'strRequirement',
  'stealthCheck',
  'baseWeapon',
  'requiresAttunement',
  'attunementDescription',
  'description',
  'hasCharges',
  'charges',
  'chargeReset',
  'chargeResetDescription',
  'notes',
  'weight',
  'tags',
];

export const DDB_MODIFIER_FIELDS = [
  'type',
  'subtype',
  'value',
  'abilityScore',
  'diceCount',
  'dieType',
  'additionalBonusTypes',
  'details',
  'durationInterval',
  'durationUnit',
  'requiresAttunement',
];

export const DDB_SPELL_FIELDS = [
  'spell',
  'charges',
  'minCharges',
  'maxCharges',
  'saveDc',
  'castAtLevel',
  'details',
  'legacy',
];

// DDB's blank "Create a Magic Item" form — where the importer starts.
export const DDB_CREATE_ITEM_URL = 'https://www.dndbeyond.com/homebrew/creations/create-magic-item';

// Only https://www.dndbeyond.com/ links are stored or rendered: ddb_url is
// shown to the DM as a link, and an agent can write it.
export function isDdbUrl(value) {
  return typeof value === 'string' && value.length <= 500 && /^https:\/\/www\.dndbeyond\.com\//.test(value);
}
